// Sponsor links stay direct; this module only adds best-effort attribution that
// is unable to block or replace the browser's navigation.
export type SponsorTapSurface = "live" | "event_page";

export interface SponsorTapBeaconInput {
  communityAdminUrl: string;
  sponsorId: string;
  eventId: string;
  surface: SponsorTapSurface;
}

interface SponsorTapBeaconRequest extends SponsorTapBeaconInput {
  nonce: string;
}

interface SponsorTapTransport {
  sendBeacon?: (url: string, data: BodyInit | null) => boolean;
  fetch?: (url: string, init: RequestInit) => Promise<unknown>;
}

const SPONSOR_TAP_PATH = "/api/public/sponsor-tap";

// One nonce per browser module load lets the server absorb duplicate click
// delivery without conflating different attendees behind the same IP address.
const PAGE_LOAD_NONCE = globalThis.crypto.randomUUID();

export function buildSponsorTapBeaconRequest({
  communityAdminUrl,
  sponsorId,
  eventId,
  surface,
  nonce,
}: SponsorTapBeaconRequest): {
  beaconUrl: string;
  body: URLSearchParams;
} {
  const normalizedBaseUrl = communityAdminUrl.trim().replace(/\/+$/, "");
  const beaconUrl = new URL(SPONSOR_TAP_PATH, `${normalizedBaseUrl}/`).toString();
  const body = new URLSearchParams({
    s: sponsorId,
    e: eventId,
    surface,
    n: nonce,
  });

  return { beaconUrl, body };
}

function browserTransport(): SponsorTapTransport {
  return {
    sendBeacon:
      typeof navigator !== "undefined" &&
      typeof navigator.sendBeacon === "function"
        ? (url, data) => navigator.sendBeacon(url, data)
        : undefined,
    fetch:
      typeof globalThis.fetch === "function"
        ? (url, init) => globalThis.fetch(url, init)
        : undefined,
  };
}

export function sendSponsorTapBeacon(
  input: SponsorTapBeaconInput,
  transport: SponsorTapTransport = browserTransport(),
): void {
  let request: ReturnType<typeof buildSponsorTapBeaconRequest>;
  try {
    request = buildSponsorTapBeaconRequest({
      ...input,
      nonce: PAGE_LOAD_NONCE,
    });
  } catch {
    return;
  }

  try {
    if (transport.sendBeacon?.(request.beaconUrl, request.body) === true) {
      return;
    }
  } catch {
    // A rejected beacon gets the same keepalive fallback as an unavailable one.
  }

  try {
    const fallback = transport.fetch?.(request.beaconUrl, {
      method: "POST",
      body: request.body,
      keepalive: true,
    });
    void fallback?.catch(() => undefined);
  } catch {
    // Attribution is deliberately fire-and-forget; sponsor navigation must win.
  }
}
